import axios from "./axiosInstance";

const AFFILIATE_CACHE_PREFIX = "affiliateResolve:";
const affiliateInFlight = new Map();

function resolveAffiliateOnce(afid) {
  const key = AFFILIATE_CACHE_PREFIX + afid;
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) return Promise.resolve({ success: true, data: JSON.parse(cached) });
  } catch {
    // storage blocked or bad JSON — fall through to the network
  }
  if (!affiliateInFlight.has(afid)) {
    const request = axios
      .get(`affiliates/campaigns/resolve?afid=${encodeURIComponent(afid)}`)
      .then((res) => {
        try {
          if (res?.data) sessionStorage.setItem(key, JSON.stringify(res.data));
        } catch {
          // ignore — the in-memory promise still dedupes this page
        }
        return res;
      })
      .catch((err) => {
        // A failed link may be fixed later; let the next call try again.
        affiliateInFlight.delete(afid);
        throw err;
      });
    affiliateInFlight.set(afid, request);
  }
  return affiliateInFlight.get(afid);
}

// ─── Nitya Seva API calls ────────────────────────────────────────────────────
const nityaSevaAPI = {
  getTiers: () => axios.get("nityaseva-tiers/public"),

  getAltars: (params = { sevaType: "ARTI" }) =>
    axios.get("altar-names/public", { params }),

  getVigrahaAvailability: (tierId, altar) =>
    axios.get("nityaseva/vigraha-availability", {
      params: { tierId, ...(altar ? { altar } : {}) },
    }),

  // Every resolve call is counted as a campaign visit on the backend, so an
  // affiliate link is resolved once per browser session: React's dev-mode
  // double effects, a page that embeds the checkout twice, or moving on to
  // the booking page all reuse the first answer instead of adding visits.
  resolveAffiliate: (afid) => resolveAffiliateOnce(afid),

  checkout: (body) => axios.post("nityaseva/checkout", body),

  // Step 1 — Register (returns enrollmentId)
  register: (body) => axios.post("nityaseva/register", body),

  // Step 2 — Verify OTP (returns JWT token)
  verifyOtp: (body) => axios.post("nityaseva/verify-otp", body),

  // Step 2b — Resend OTP (register flow)
  resendOtp: (enrollmentId) =>
    axios.post(`nityaseva/resend-otp/${enrollmentId}`),

  // Step 3 — Submit enrollment form (needs JWT in header)
  submitEnrollment: (body, token) =>
    axios.post("nityaseva/enroll", body, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  // Step 4 — Initiate Razorpay payment
  initiatePayment: (body, token) =>
    axios.post("nityaseva/payment/initiate", body, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  // Step 5 — Verify Razorpay payment
  verifyPayment: (body, token) => axios.post("nityaseva/payment/verify", body),

  // ─── Member Portal (login for existing Nitya Sevaks) ───────────────────
  memberLoginPassword: (body) =>
    axios.post("nityaseva/member/login-password", body),

  // Login Step 1 — send mobile, get OTP
  memberLogin: (body) => axios.post("nityaseva/member/login", body),

  // Login Step 1b — resend OTP
  memberResendOtp: (body) => axios.post("nityaseva/member/resend-otp", body),

  // Login Step 2 — verify OTP, get JWT token
  memberVerifyOtp: (body) => axios.post("nityaseva/member/verify-otp", body),

  // Refresh JWT token
  memberRefresh: (body) => axios.post("nityaseva/member/refresh", body),

  // Logout (needs JWT in header)
  memberLogout: (token) =>
    axios.post(
      "nityaseva/member/logout",
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    ),

  // Get logged-in member's profile (needs JWT in header)
  getMemberProfile: (token) =>
    axios.get("nityaseva/member/profile", {
      headers: { Authorization: `Bearer ${token}` },
    }),

  // Update logged-in member's profile (needs JWT in header)
  updateMemberProfile: (body, token) =>
    axios.patch("nityaseva/member/profile", body, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  getMySubscriptions: (token) =>
    axios.get("nityaseva/member/subscriptions", {
      headers: { Authorization: `Bearer ${token}` },
    }),

  initiateMemberSubscription: (body, token) =>
    axios.post("nityaseva/member/subscriptions/initiate", body, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  createMemberSubscription: (body, token) =>
    axios.post("nityaseva/member/subscriptions/initiate", body, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  verifyMemberSubscription: (body, token) =>
    axios.post("nityaseva/member/subscriptions/verify", body, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

export default nityaSevaAPI;
